import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { MapContainer, Marker, Polygon, Popup, TileLayer, useMap } from 'react-leaflet';
import GetAppIcon from '@material-ui/icons/GetApp';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import PlaceIcon from '@material-ui/icons/Place';
import L from 'leaflet';
import { Button } from '@spark/ui-components/components/Button';
import BottomStatusFilter from '@spark/ui-components/components/BottomStatusFilter';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import PosicoesFrotaMapFilter from './PosicoesFrotaMapFilter';
import { PositionRefreshIndicator } from './RefreshIndicator';
import { exportToExcel } from '../services/disponibilidadeFrotaPatioApi';
import {
  mapContainerHeight,
  mapFallbackCenter,
  mapFallbackZoom,
  singleVehicleMapZoom
} from '../utils/constants';
import { safeAreaConfig } from '../config/safeAreaConfig';
import {
  formatPopupDateTime,
  formatPositionDateTime,
  getDisponibilidadeTextColor,
  getPositionSourceLabel,
  getStatusPatioTextColor
} from '../utils/formatters';
import {
  ButtonIconWrap,
  VehicleMapPopupStyles,
  PanelSection,
  SectionHeader,
  SectionTitle
} from '../DisponibilidadeFrota-styled';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

const buildMarkerIconUrl = fillColor => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
    <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 10.2 12.5 28.5 12.5 28.5S25 22.7 25 12.5C25 5.6 19.4 0 12.5 0z" fill="${fillColor}"/>
    <circle cx="12.5" cy="12.5" r="5.5" fill="#ffffff"/>
  </svg>
`)}`;

const createVehicleMarkerIcon = fillColor => L.icon({
  iconUrl: buildMarkerIconUrl(fillColor),
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const availableVehicleMarkerIcon = createVehicleMarkerIcon('#16a34a');
const unavailableVehicleMarkerIcon = createVehicleMarkerIcon('#dc2626');
const outsideSafeAreaVehicleMarkerIcon = createVehicleMarkerIcon('#eab308');

const isVehiclePatioDisponivel = vehicle => {
  const statusPatioLabel = String(vehicle?.statusPatioLabel || '').trim().toLowerCase();
  const statusPatioVariant = String(vehicle?.statusPatioVariant || '').trim().toLowerCase();

  return statusPatioLabel === 'disponiveis' || statusPatioVariant === 'success';
};

const isVehicleYellowMarker = (vehicle, safeAreaPolygon) => (
  isVehicleOutsideSafeArea(vehicle, safeAreaPolygon) && isVehiclePatioDisponivel(vehicle)
);

const isPointInsidePolygon = (latitude, longitude, polygon) => {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;

  let isInside = false;

  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
    const [currentLat, currentLng] = polygon[index];
    const [previousLat, previousLng] = polygon[previousIndex];
    const intersects = ((currentLat > latitude) !== (previousLat > latitude))
      && (longitude < ((previousLng - currentLng) * (latitude - currentLat)) / ((previousLat - currentLat) || Number.EPSILON) + currentLng);

    if (intersects) {
      isInside = !isInside;
    }
  }

  return isInside;
};

const isVehicleOutsideSafeArea = (vehicle, safeAreaPolygon) => {
  if (!vehicle || !safeAreaPolygon.length) return false;
  return !isPointInsidePolygon(vehicle.latitude, vehicle.longitude, safeAreaPolygon);
};

const getVehicleMarkerIcon = (vehicle, safeAreaPolygon) => {
  if (isVehicleYellowMarker(vehicle, safeAreaPolygon)) {
    return outsideSafeAreaVehicleMarkerIcon;
  }

  return vehicle?.disponivel === true && vehicle?.statusPatioVariant === 'success'
    ? availableVehicleMarkerIcon
    : unavailableVehicleMarkerIcon;
};

const MapViewportController = ({ vehicles, focusKey, safeAreaPolygon, safeAreaFocusRequestKey }) => {
  const map = useMap();
  const lastFocusKeyRef = useRef(null);
  const lastVehicleCountRef = useRef(0);
  const lastSafeAreaCountRef = useRef(0);
  const lastSafeAreaFocusRequestKeyRef = useRef(0);

  useEffect(() => {
    const shouldFocusSafeAreaOnly = safeAreaFocusRequestKey > 0
      && lastSafeAreaFocusRequestKeyRef.current !== safeAreaFocusRequestKey;

    lastSafeAreaFocusRequestKeyRef.current = safeAreaFocusRequestKey;

    if (shouldFocusSafeAreaOnly && safeAreaPolygon.length) {
      const safeAreaBounds = L.latLngBounds(safeAreaPolygon);

      map.fitBounds(safeAreaBounds.pad(0.15), {
        animate: true
      });
      return;
    }

    const hadVehicles = lastVehicleCountRef.current > 0;
    const hasVehicles = vehicles.length > 0;
    const hadSafeArea = lastSafeAreaCountRef.current > 0;
    const hasSafeArea = safeAreaPolygon.length > 0;
    const shouldRefocus = lastFocusKeyRef.current !== focusKey
      || (!hadVehicles && hasVehicles)
      || (!hadSafeArea && hasSafeArea);

    lastFocusKeyRef.current = focusKey;
    lastVehicleCountRef.current = vehicles.length;
    lastSafeAreaCountRef.current = safeAreaPolygon.length;

    if (!shouldRefocus) {
      return;
    }

    if (!vehicles.length && !safeAreaPolygon.length) {
      map.setView(mapFallbackCenter, mapFallbackZoom);
      return;
    }

    if (vehicles.length === 1 && !safeAreaPolygon.length) {
      map.setView([vehicles[0].latitude, vehicles[0].longitude], singleVehicleMapZoom);
      return;
    }

    const points = [
      ...vehicles.map(vehicle => [vehicle.latitude, vehicle.longitude]),
      ...safeAreaPolygon
    ];
    const bounds = L.latLngBounds(points);

    map.fitBounds(bounds.pad(0.2), {
      animate: false
    });
  }, [focusKey, map, safeAreaFocusRequestKey, safeAreaPolygon, vehicles]);

  return null;
};

const MapPopupRow = ({ label, value, valueStyle }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '84px minmax(0, 1fr)',
    gap: '8px',
    alignItems: 'start'
  }}>
    <span style={{ color: '#64748b', fontWeight: 700, fontSize: '12px', whiteSpace: 'nowrap' }}>{label}:</span>
    <span style={{
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: 1.35,
      wordBreak: 'normal',
      overflowWrap: 'break-word',
      ...valueStyle
    }}
    >
      {value || '-'}
    </span>
  </div>
);

const MapPopupSection = ({ icon: Icon, title, children, style }) => (
  <div style={{
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    background: '#f8fafc',
    ...style
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginBottom: '6px',
      color: '#1e3a8a',
      fontSize: '11px',
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    }}>
      <Icon style={{ fontSize: '14px' }} />
      <span>{title}</span>
    </div>
    <div style={{ display: 'grid', gap: '4px' }}>
      {children}
    </div>
  </div>
);

const getPositionStatusExportLabel = status => {
  if (status === 'missing') return 'Sem posicao';
  if (status === 'loading' || status === 'unknown') return 'Consultando posicao';
  if (status === 'found') return 'Posicao encontrada';
  return status || '-';
};

const buildMapExportRows = vehiclesToExport => vehiclesToExport.map(vehicle => ({
  ID: vehicle.id ?? vehicle.idVeiculo ?? '',
  Frota: vehicle.frota,
  Veiculo: vehicle.veiculo,
  Placa: vehicle.placa,
  Gestor: vehicle.gestor,
  Motorista: vehicle.motorista,
  'Modelo Veicular': vehicle.modeloVeicular,
  'Segmento Veiculo': vehicle.segmentoVeiculo,
  'Tipo da Operacao': vehicle.tipoOperacao,
  Cargas: vehicle.cargasResumo,
  Status: vehicle.disponivelLabel,
  'Status Patio': vehicle.statusPatioLabel,
  Regiao: vehicle.regiao,
  Local: vehicle.local,
  'Fonte Posicao': getPositionSourceLabel(vehicle.integracao),
  'Atualizacao Posicao': formatPositionDateTime(vehicle.dataPosicao),
  'Status da Posicao': getPositionStatusExportLabel(vehicle.status),
  Latitude: vehicle.latitude,
  Longitude: vehicle.longitude
}));

const PosicoesFrotaMap = ({
  allVehicles = [],
  vehicles = [],
  foundCount,
  loadingCount,
  missingCount,
  nextRefreshInMs,
  positionLastUpdatedAt,
  positionRefreshPaused,
  positionLoading,
  onPositionRefresh,
  onTogglePositionRefreshPaused,
  regionOptions,
  frotaOptions,
  selectedRegion,
  onRegionChange,
  selectedFrotaSearch,
  onFrotaSearchChange
}) => {
  const safeAreaPolygon = safeAreaConfig?.polygon || [];
  const [safeAreaFocusRequestKey, setSafeAreaFocusRequestKey] = useState(0);
  const [allVehiclesFocusRequestKey, setAllVehiclesFocusRequestKey] = useState(0);
  const [showOnlyYellowVehicles, setShowOnlyYellowVehicles] = useState(false);
  const [selectedMapTopCard, setSelectedMapTopCard] = useState('todos-veiculos');
  const yellowVehiclesCount = useMemo(
    () => vehicles.filter(vehicle => isVehicleYellowMarker(vehicle, safeAreaPolygon)).length,
    [safeAreaPolygon, vehicles]
  );
  const safeAreaVehiclesCount = useMemo(
    () => vehicles.filter(vehicle => !isVehicleOutsideSafeArea(vehicle, safeAreaPolygon)).length,
    [safeAreaPolygon, vehicles]
  );
  const yellowVehicles = useMemo(
    () => vehicles.filter(vehicle => isVehicleYellowMarker(vehicle, safeAreaPolygon)),
    [safeAreaPolygon, vehicles]
  );
  const safeAreaVehicles = useMemo(
    () => vehicles.filter(vehicle => !isVehicleOutsideSafeArea(vehicle, safeAreaPolygon)),
    [safeAreaPolygon, vehicles]
  );
  const displayedVehicles = showOnlyYellowVehicles ? yellowVehicles : vehicles;
  const displayedFoundCount = showOnlyYellowVehicles ? yellowVehicles.length : foundCount;
  const displayedLoadingCount = showOnlyYellowVehicles ? 0 : loadingCount;
  const displayedMissingCount = showOnlyYellowVehicles ? 0 : missingCount;
  const exportConfig = useMemo(() => {
    if (selectedMapTopCard === 'area-segura') {
      return {
        buttonLabel: 'Exportar area segura',
        fileName: 'mapa-frotas-area-segura.xlsx',
        sheetName: 'Area Segura',
        rows: buildMapExportRows(safeAreaVehicles)
      };
    }

    if (selectedMapTopCard === 'inconsistencias-status') {
      return {
        buttonLabel: 'Exportar inconsistencias status',
        fileName: 'mapa-frotas-inconsistencias-status.xlsx',
        sheetName: 'Inconsistencias Status',
        rows: buildMapExportRows(yellowVehicles)
      };
    }

    return {
      buttonLabel: 'Exportar todos veiculos',
      fileName: 'mapa-frotas-todos-veiculos.xlsx',
      sheetName: 'Todos Veiculos',
      rows: buildMapExportRows(allVehicles)
    };
  }, [allVehicles, safeAreaVehicles, selectedMapTopCard, yellowVehicles]);

  const handleFocusSafeArea = () => {
    setSafeAreaFocusRequestKey(prev => prev + 1);
  };

  const handleFocusAllVehicles = () => {
    setAllVehiclesFocusRequestKey(prev => prev + 1);
  };

  const handleSafeAreaCardChange = value => {
    const shouldActivate = value === 'area-segura';

    setSelectedMapTopCard(shouldActivate ? 'area-segura' : 'todos-veiculos');
    setShowOnlyYellowVehicles(false);

    if (shouldActivate) {
      handleFocusSafeArea();
      return;
    }

    handleFocusAllVehicles();
  };

  const handleAllVehiclesCardChange = () => {
    setSelectedMapTopCard('todos-veiculos');
    setShowOnlyYellowVehicles(false);
    handleFocusAllVehicles();
  };

  const handleYellowFilterChange = value => {
    const shouldActivate = value === 'inconsistencias-status';

    setSelectedMapTopCard(shouldActivate ? 'inconsistencias-status' : 'todos-veiculos');
    setShowOnlyYellowVehicles(shouldActivate);

    if (!shouldActivate) {
      handleFocusAllVehicles();
    }
  };

  const handleExportMapVehicles = async () => {
    if (!exportConfig.rows.length) return;
    await exportToExcel(exportConfig.rows, exportConfig.fileName, exportConfig.sheetName);
  };

  return (
    <PanelSection className="mt-4">
      <VehicleMapPopupStyles />
      <SectionHeader style={{ marginTop: 0 }}>
        <SectionTitle>Mapa Frotas</SectionTitle>
        <Button variant="success" onClick={handleExportMapVehicles} disabled={!exportConfig.rows.length}>
          <ButtonIconWrap>
            <GetAppIcon style={{ fontSize: 16 }} />
          </ButtonIconWrap>
          {exportConfig.buttonLabel}
        </Button>
      </SectionHeader>

      <Row className="mb-3">
        <Col lg="auto" md={6} className="mb-3 mb-lg-0">
          <BottomStatusFilter
            options={[{
              value: 'todos-veiculos',
              label: 'TODOS VEICULOS',
              icon: 'truck',
              summaryLines: [
                `Quantidade: ${foundCount}`,
                `Sem posicao: ${missingCount}`
              ]
            }]}
            value={selectedMapTopCard === 'todos-veiculos' ? 'todos-veiculos' : ''}
            onChange={handleAllVehiclesCardChange}
          />
        </Col>

        <Col lg="auto" md={6} className="mb-3 mb-lg-0">
          <BottomStatusFilter
            options={[{
              value: 'area-segura',
              label: 'AREA SEGURA',
              icon: 'map-marker-path',
              summaryLines: [
                safeAreaConfig.name,
                `Quantidade: ${safeAreaVehiclesCount}`
              ]
            }]}
            value={selectedMapTopCard === 'area-segura' ? 'area-segura' : ''}
            onChange={handleSafeAreaCardChange}
          />
        </Col>

        <Col lg="auto" md={6} className="mb-3 mb-lg-0">
          <BottomStatusFilter
            options={[{
              value: 'inconsistencias-status',
              label: 'INCONSISTENCIAS STATUS',
              icon: 'filter',
              summaryLines: [
                'Incosistencia status rabbot',
                `Quantidade: ${yellowVehiclesCount}`
              ]
            }]}
            value={selectedMapTopCard === 'inconsistencias-status' ? 'inconsistencias-status' : ''}
            onChange={handleYellowFilterChange}
          />
        </Col>
      </Row>

      <PosicoesFrotaMapFilter
        regionOptions={regionOptions}
        frotaOptions={frotaOptions}
        selectedRegion={selectedRegion}
        selectedFrotaSearch={selectedFrotaSearch}
        onRegionChange={onRegionChange}
        onFrotaSearchChange={onFrotaSearchChange}
      />

      <div className="mb-3" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PositionRefreshIndicator
          enabled={true}
          onRefresh={onPositionRefresh}
          loading={positionLoading}
          lastUpdatedAt={positionLastUpdatedAt}
          paused={positionRefreshPaused}
          onTogglePaused={onTogglePositionRefreshPaused}
          remainingMs={nextRefreshInMs}
        />
      </div>

      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        overflow: 'hidden',
        background: '#ffffff'
      }}>
        <div style={{ height: mapContainerHeight, width: '100%' }}>
        <MapContainer
          center={mapFallbackCenter}
          zoom={mapFallbackZoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            <MapViewportController
              vehicles={displayedVehicles}
              safeAreaPolygon={safeAreaPolygon}
              safeAreaFocusRequestKey={safeAreaFocusRequestKey}
              focusKey={`${selectedRegion?.value || 'all-regions'}-${selectedFrotaSearch || ''}-${showOnlyYellowVehicles ? 'yellow-only' : 'all'}-${selectedMapTopCard || 'no-top-card'}-${allVehiclesFocusRequestKey}`}
            />
            {safeAreaPolygon.length ? (
              <Polygon positions={safeAreaPolygon} pathOptions={safeAreaConfig.style}>
                <Popup>
                  <div style={{ minWidth: '180px', textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '4px', textAlign: 'center' }}>
                      {safeAreaConfig.name}
                    </div>
                  </div>
                </Popup>
              </Polygon>
            ) : null}
            {displayedVehicles.map(vehicle => (
              <Marker
                key={`${vehicle.frota}-${vehicle.placa}-${vehicle.latitude}-${vehicle.longitude}`}
                position={[vehicle.latitude, vehicle.longitude]}
                icon={getVehicleMarkerIcon(vehicle, safeAreaPolygon)}
              >
                <Popup maxWidth={760} className="vehicle-map-popup">
                  <div style={{
                    minWidth: '620px',
                    maxWidth: '680px',
                    display: 'grid',
                    gap: '8px',
                    paddingTop: '12px',
                    paddingLeft: '6px',
                    paddingRight: '10px',
                    paddingBottom: '4px'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 1.1fr)',
                      gap: '12px',
                      alignItems: 'stretch'
                    }}>
                      <MapPopupSection icon={LocalShippingIcon} title="Veículo" style={{ height: '100%' }}>
                        <MapPopupRow label="Frota" value={vehicle.frota} />
                        <MapPopupRow label="Veículo" value={vehicle.veiculo} />
                        <MapPopupRow label="Modelo" value={vehicle.modeloVeicular} />
                        <MapPopupRow label="Segmento" value={vehicle.segmentoVeiculo} />
                        <MapPopupRow label="Operação" value={vehicle.tipoOperacao} />
                        <MapPopupRow label="Cargas" value={vehicle.cargasResumo} />
                        <MapPopupRow
                          label="Status"
                          value={vehicle.disponivelLabel}
                          valueStyle={{ color: getDisponibilidadeTextColor(vehicle.disponivel) }}
                        />
                        <MapPopupRow
                          label="Pátio"
                          value={vehicle.statusPatioLabel}
                          valueStyle={{ color: getStatusPatioTextColor(vehicle.statusPatioVariant) }}
                        />
                      </MapPopupSection>

                      <div style={{ display: 'grid', gap: '10px' }}>
                        <MapPopupSection icon={PersonIcon} title="Responsáveis">
                          <MapPopupRow label="Gestor" value={vehicle.gestor} />
                          <MapPopupRow label="Motorista" value={vehicle.motorista} />
                        </MapPopupSection>

                        <MapPopupSection icon={PlaceIcon} title="Posição">
                          <MapPopupRow label="Região" value={vehicle.regiao} />
                          <MapPopupRow label="Local" value={vehicle.local} />
                          <MapPopupRow label="Fonte" value={getPositionSourceLabel(vehicle.integracao)} />
                          <MapPopupRow label="Atualização" value={formatPositionDateTime(vehicle.dataPosicao)} />
                        </MapPopupSection>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      {!displayedVehicles.length ? (
        <div className="small text-muted mt-3">
          {showOnlyYellowVehicles
            ? 'Nenhuma posicao amarela encontrada para os filtros atuais.'
            : 'Aguardando as primeiras posições com coordenadas válidas para exibir os veículos no mapa.'}
        </div>
      ) : null}
    </PanelSection>
  );
};

export default PosicoesFrotaMap;
